import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, ArrowDown, Plus, Trash2, RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';
import type { SiteSettings } from '../../lib/types';

interface FooterLink { label: string; href: string }
interface FooterColumn { title: string; links: FooterLink[] }
interface FooterLocale { tagline: string; columns: FooterColumn[]; bottomText: string }
interface FooterData { es: FooterLocale; en: FooterLocale }

type FooterSel =
  | { kind: 'column'; ci: number }
  | { kind: 'link'; ci: number; li: number }
  | null;

function emptyLocale(): FooterLocale { return { tagline: '', columns: [], bottomText: '' }; }
function emptyFooter(): FooterData { return { es: emptyLocale(), en: emptyLocale() }; }

// Normaliza un locale parcial/ausente a la forma completa. Garantiza que
// `columns` (y los links de cada columna) siempre sean arrays, para que el
// editor no truene si el footer guardado está vacío o incompleto.
function normalizeLocale(raw: unknown): FooterLocale {
  const o = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  const columns = Array.isArray(o.columns) ? o.columns.map((c) => {
    const col = (c && typeof c === 'object') ? c as Record<string, unknown> : {};
    return {
      title: typeof col.title === 'string' ? col.title : '',
      links: Array.isArray(col.links) ? col.links.map((l) => {
        const lk = (l && typeof l === 'object') ? l as Record<string, unknown> : {};
        return {
          label: typeof lk.label === 'string' ? lk.label : '',
          href: typeof lk.href === 'string' ? lk.href : '',
        };
      }) : [],
    };
  }) : [];
  return {
    tagline: typeof o.tagline === 'string' ? o.tagline : '',
    columns,
    bottomText: typeof o.bottomText === 'string' ? o.bottomText : '',
  };
}
function parseFooter(json: string | null | undefined): FooterData {
  let raw: unknown;
  try { raw = JSON.parse(json ?? ''); } catch { raw = null; }
  const o = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  return { es: normalizeLocale(o.es), en: normalizeLocale(o.en) };
}

export default function FooterEditorPage() {
  const navigate = useNavigate();

  const [footer, setFooter] = useState<FooterData>(emptyFooter());
  const [locale, setLocale] = useState<'es' | 'en'>('es');
  const [selected, setSelected] = useState<FooterSel>(null);
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [noPages, setNoPages] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const previewBaseRef = useRef<string | null>(null);
  const tokenAtRef = useRef(0);
  const homePageIdRef = useRef<string | null>(null);

  async function ensureToken(pageId: string) {
    if (previewBaseRef.current && Date.now() - tokenAtRef.current < 13 * 60 * 1000) return;
    const { url } = await api.preview.getUrl(pageId);
    previewBaseRef.current = url;
    tokenAtRef.current = Date.now();
  }

  async function loadPreview(pageId?: string) {
    const id = pageId ?? homePageIdRef.current;
    if (!id) return;
    try {
      await ensureToken(id);
      if (!previewBaseRef.current) return;
      setPreviewSrc(`${previewBaseRef.current}&v=${Date.now()}`);
    } catch {
      // preview best-effort
    }
  }

  useEffect(() => {
    api.settings.get().then((s) => {
      if (!s) return;
      setSettings(s);
      setFooter(parseFooter(s.footerJson));
    }).catch(console.error);

    api.pages.list().then(async (pages) => {
      if (pages.length === 0) { setNoPages(true); return; }
      const home = pages.find((p) => p.slug === 'home' || p.slug === '/') ?? pages[0];
      homePageIdRef.current = home.id;
      await loadPreview(home.id);
    }).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loc = footer[locale];

  function updateFooter(newFooter: FooterData) {
    setFooter(newFooter);
    setIsDirty(true);
  }
  function updateLocale(patch: Partial<FooterLocale>) {
    updateFooter({ ...footer, [locale]: { ...loc, ...patch } });
  }
  function updateColumn(ci: number, patch: Partial<FooterColumn>) {
    updateLocale({ columns: loc.columns.map((col, i) => i === ci ? { ...col, ...patch } : col) });
  }
  function updateLink(ci: number, li: number, patch: Partial<FooterLink>) {
    updateColumn(ci, { links: loc.columns[ci].links.map((lk, i) => i === li ? { ...lk, ...patch } : lk) });
  }

  function addColumn() {
    const newCols = [...loc.columns, { title: '', links: [] }];
    updateLocale({ columns: newCols });
    setSelected({ kind: 'column', ci: newCols.length - 1 });
  }
  function removeColumn(ci: number) {
    updateLocale({ columns: loc.columns.filter((_, i) => i !== ci) });
    setSelected(null);
  }
  function moveColumn(ci: number, dir: -1 | 1) {
    const next = ci + dir;
    if (next < 0 || next >= loc.columns.length) return;
    const cols = [...loc.columns];
    [cols[ci], cols[next]] = [cols[next], cols[ci]];
    updateLocale({ columns: cols });
    setSelected({ kind: 'column', ci: next });
  }
  function addLink(ci: number) {
    const col = loc.columns[ci];
    const newLinks = [...col.links, { label: '', href: '' }];
    updateColumn(ci, { links: newLinks });
    setSelected({ kind: 'link', ci, li: newLinks.length - 1 });
  }
  function removeLink(ci: number, li: number) {
    updateColumn(ci, { links: loc.columns[ci].links.filter((_, i) => i !== li) });
    setSelected(null);
  }

  const save = useCallback(async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.settings.update({ ...settings, footerJson: JSON.stringify(footer) });
      setIsDirty(false);
      setSavedAt(new Date());
      await loadPreview();
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, footer]);

  const saveLabel = saving ? 'Guardando…'
    : isDirty ? 'Cambios sin guardar'
    : savedAt ? `Guardado · ${savedAt.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`
    : 'Sin cambios';

  const selCol = selected?.kind === 'column' ? loc.columns[selected.ci] ?? null : null;
  const selLink = selected?.kind === 'link' ? (loc.columns[selected.ci]?.links[selected.li] ?? null) : null;

  return (
    <div className="flex flex-col h-screen bg-ui-bg text-ui-text overflow-hidden">

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-3 py-[10px] border-b border-ui-border bg-ui-surface shrink-0">
        <button onClick={() => navigate('/site?tab=paginas')}
          className="rounded-md border border-ui-border px-3 py-1.5 text-sm text-ui-text-muted hover:text-ui-text hover:border-ui-border-strong transition-colors">
          ← Páginas
        </button>
        <span className="text-sm font-semibold text-ui-text">Pie de página</span>
        <span className="text-xs text-ui-text-subtle ml-1">{saveLabel}</span>
        <div className="ml-auto">
          <button onClick={save} disabled={saving || !isDirty || !settings}
            className="px-4 py-1.5 bg-ui-accent text-ui-text-inverse text-sm font-medium rounded-md disabled:opacity-40 hover:opacity-90 transition-opacity">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left: Navigator ─────────────────────────────────────────────── */}
        <div className="w-[260px] shrink-0 flex flex-col border-r border-ui-border bg-ui-surface">
          <div className="p-4 border-b border-ui-border space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ui-text">Estructura</h2>
              <span className="text-xs text-ui-text-subtle">{loc.columns.length} col.</span>
            </div>
            <div className="flex gap-1">
              {(['es', 'en'] as const).map((l) => (
                <button key={l} onClick={() => { setLocale(l); setSelected(null); }}
                  className={`flex-1 py-1 text-xs font-semibold rounded transition-colors ${
                    locale === l ? 'bg-ui-accent text-ui-text-inverse' : 'text-ui-text-muted hover:text-ui-text'
                  }`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-4">

            {/* Tagline */}
            <div>
              <label className="block text-xs text-ui-text-subtle uppercase tracking-wider mb-1.5">Tagline</label>
              <input value={loc.tagline} onChange={(e) => updateLocale({ tagline: e.target.value })}
                placeholder="Tu lema o descripción breve"
                className="w-full bg-ui-bg border border-ui-border rounded px-3 py-1.5 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
            </div>

            {/* Columnas */}
            <div>
              <label className="block text-xs text-ui-text-subtle uppercase tracking-wider mb-1.5">Columnas</label>
              <div className="space-y-2">
                {loc.columns.map((col, ci) => {
                  const isSelCol = selected?.kind === 'column' && selected.ci === ci;
                  return (
                    <div key={ci} className={`rounded-xl border transition-colors ${isSelCol ? 'border-ui-accent bg-ui-accent/10' : 'border-ui-border'}`}>
                      {/* Column header */}
                      <div
                        role="button" tabIndex={0}
                        onClick={() => setSelected({ kind: 'column', ci })}
                        onKeyDown={(e) => e.key === 'Enter' && setSelected({ kind: 'column', ci })}
                        className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                      >
                        <span className="flex-1 text-sm font-medium text-ui-text truncate">
                          {col.title || <span className="text-ui-text-subtle italic text-xs">Sin título</span>}
                        </span>
                        <div className="flex items-center gap-0.5 shrink-0">
                          <button onClick={(e) => { e.stopPropagation(); moveColumn(ci, -1); }} disabled={ci === 0}
                            className="p-1 text-ui-text-subtle hover:text-ui-text disabled:opacity-30 transition-colors">
                            <ArrowUp size={11} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); moveColumn(ci, 1); }} disabled={ci === loc.columns.length - 1}
                            className="p-1 text-ui-text-subtle hover:text-ui-text disabled:opacity-30 transition-colors">
                            <ArrowDown size={11} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); removeColumn(ci); }}
                            className="p-1 text-ui-text-subtle hover:text-red-500 transition-colors">
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </div>

                      {/* Links */}
                      <div className="px-3 pb-2 space-y-1">
                        {col.links.map((lk, li) => {
                          const isSelLink = selected?.kind === 'link' && selected.ci === ci && selected.li === li;
                          return (
                            <div key={li}
                              role="button" tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'link', ci, li }); }}
                              onKeyDown={(e) => e.key === 'Enter' && setSelected({ kind: 'link', ci, li })}
                              className={`flex items-center gap-2 pl-2 pr-1 py-1 rounded-lg cursor-pointer transition-colors ${
                                isSelLink ? 'bg-ui-accent/20' : 'hover:bg-ui-text/[0.05]'
                              }`}>
                              <span className="flex-1 text-xs text-ui-text truncate">
                                {lk.label || <span className="text-ui-text-subtle italic">Sin etiqueta</span>}
                              </span>
                              <span className="text-xs text-ui-text-subtle font-mono truncate max-w-[60px]">{lk.href || '—'}</span>
                              <button onClick={(e) => { e.stopPropagation(); removeLink(ci, li); }}
                                className="p-0.5 text-ui-text-subtle hover:text-red-500 transition-colors shrink-0">
                                <Trash2 size={10} />
                              </button>
                            </div>
                          );
                        })}
                        <button onClick={(e) => { e.stopPropagation(); addLink(ci); }}
                          className="flex items-center gap-1 text-xs text-ui-text-subtle hover:text-ui-accent transition-colors pl-2 py-0.5">
                          <Plus size={10} /> Añadir enlace
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button onClick={addColumn}
                className="w-full mt-2 flex items-center justify-center gap-1.5 py-2 text-sm text-ui-accent hover:opacity-80 border border-dashed border-ui-border rounded-lg transition-opacity">
                <Plus size={13} /> Añadir columna
              </button>
            </div>

            {/* Texto inferior */}
            <div>
              <label className="block text-xs text-ui-text-subtle uppercase tracking-wider mb-1.5">Texto inferior</label>
              <input value={loc.bottomText} onChange={(e) => updateLocale({ bottomText: e.target.value })}
                placeholder="© 2024 Mi Empresa · Privacidad"
                className="w-full bg-ui-bg border border-ui-border rounded px-3 py-1.5 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
            </div>
          </div>
        </div>

        {/* ── Center: Preview ──────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col overflow-hidden bg-[#f5f5f5]">
          {noPages ? (
            <div className="flex-1 flex items-center justify-center px-8 text-center">
              <p className="text-sm text-ui-text-subtle">
                Crea al menos una página para ver la vista previa del pie de página.
              </p>
            </div>
          ) : previewSrc ? (
            <iframe key={previewSrc} src={previewSrc} title="Vista previa"
              className="flex-1 w-full border-0 bg-white" />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <div className="flex items-center justify-between px-4 py-2 bg-ui-surface border-t border-ui-border shrink-0">
            <span className="text-xs text-ui-text-subtle">
              {noPages ? 'Sin páginas creadas' : 'Guarda para ver los cambios · Desplázate hasta abajo para el pie'}
            </span>
            {previewSrc && (
              <button onClick={() => loadPreview()}
                className="flex items-center gap-1.5 text-xs text-ui-accent hover:opacity-80 transition-opacity">
                <RefreshCw size={11} /> Recargar
              </button>
            )}
          </div>
        </div>

        {/* ── Right: Inspector ─────────────────────────────────────────────── */}
        <div className="w-[280px] shrink-0 flex flex-col border-l border-ui-border bg-ui-surface">
          <div className="p-4 border-b border-ui-border">
            <h2 className="text-sm font-semibold text-ui-text">Inspector</h2>
          </div>

          <div className="flex-1 overflow-y-auto p-4">

            {/* Column selected */}
            {selCol && selected?.kind === 'column' && (
              <div className="space-y-4">
                <p className="text-xs text-ui-text-subtle uppercase tracking-wider font-medium">
                  Columna {selected.ci + 1} de {loc.columns.length}
                </p>
                <div>
                  <label className="block text-sm text-ui-text-muted mb-1">Título de la columna</label>
                  <input value={selCol.title}
                    onChange={(e) => updateColumn(selected.ci, { title: e.target.value })}
                    placeholder="Empresa"
                    className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
                </div>
                <p className="text-xs text-ui-text-subtle">
                  {selCol.links.length} {selCol.links.length === 1 ? 'enlace' : 'enlaces'} en esta columna.
                </p>
                <div className="pt-2 border-t border-ui-border">
                  <button onClick={() => removeColumn(selected.ci)}
                    className="flex items-center gap-1.5 text-sm text-ui-danger hover:opacity-80 transition-colors">
                    <Trash2 size={13} /> Eliminar columna
                  </button>
                </div>
              </div>
            )}

            {/* Link selected */}
            {selLink && selected?.kind === 'link' && (
              <div className="space-y-4">
                <p className="text-xs text-ui-text-subtle uppercase tracking-wider font-medium">
                  Enlace {selected.li + 1} · {loc.columns[selected.ci]?.title || `Columna ${selected.ci + 1}`}
                </p>
                <div>
                  <label className="block text-sm text-ui-text-muted mb-1">Etiqueta</label>
                  <input value={selLink.label}
                    onChange={(e) => updateLink(selected.ci, selected.li, { label: e.target.value })}
                    placeholder="Nosotros"
                    className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
                </div>
                <div>
                  <label className="block text-sm text-ui-text-muted mb-1">Ruta (href)</label>
                  <input value={selLink.href}
                    onChange={(e) => updateLink(selected.ci, selected.li, { href: e.target.value })}
                    placeholder="/nosotros"
                    className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text font-mono focus:outline-none focus:border-ui-accent" />
                </div>
                <div className="pt-2 border-t border-ui-border">
                  <button onClick={() => removeLink(selected.ci, selected.li)}
                    className="flex items-center gap-1.5 text-sm text-ui-danger hover:opacity-80 transition-colors">
                    <Trash2 size={13} /> Eliminar enlace
                  </button>
                </div>
              </div>
            )}

            {/* Nothing selected */}
            {!selCol && !selLink && (
              <div className="text-center py-8 space-y-2">
                <p className="text-sm font-medium text-ui-text-muted">Sin selección</p>
                <p className="text-xs text-ui-text-subtle">
                  Haz clic en una columna o un enlace en el navegador para editar sus propiedades.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
